/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import {
  changeHasCostDocument,
  cloneBooking,
  cloneBookingItem,
  generateBookingRecordLocator,
  getBookingDetails,
  getBookingsTable,
  getRecordLocatorForSplittedBooking,
  getSelectedBookingItemsFirstNumbers,
  postCancelBookingItem,
  postCancelSelectedBookingItems,
  postSplitBooking,
  postUnselectAllBookings,
  toggleSelectBooking,
  toggleSelectBookingItem,
  unlockBookingItemToInvoice,
} from "@/requests/booking";
import { BookingsListItem } from "@/models/api/bookings/BookingsList";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import { BookingSelectResponse } from "@/models/api/bookings/BookingSelectResponse";
import routerInstance from "@/plugins/router";
import { BookingDetails } from "@/models/api/bookings/BookingDetails";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import BookingsGroupModule from "@/store/modules/bookings/bookingsGroup";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import CustomDataTableHeader from "@/models/components/dataTables/CustomDataTableHeader";
import { CancelBookingItem } from "@/models/api/bookings/CancelBookingItem";
import PERMISSIONS from "@/static/permissions";

export interface BookingsStateInterface {
  isBookingsLoading: boolean;
  bookings: Array<BookingsListItem>;
  bookingsDataTableOptions: DataOptions;
  countBookings: number;
  bookingsFilters: null | Array<CriterionItemRequest>;
  recordStampId: null | number;
  recordStampBooking: null | BookingsListItem;
  selectedBookingsFirstNumbers: Array<string>;
  bookingCloneBaseId: null | number;
  bookingCloneNewRecordLocator: null | string;
  bookingDetailsId: null | number;
  bookingDetails: null | BookingDetails;
  bookingItemToCancelId: null | number;
  bookingItemToCancelTicketNumber: null | string;
  isEditTemp: boolean;
  editTempDataTableOptions: DataOptions;
  editTempFilters: null | Array<CriterionItemRequest>;
  tableHeaders: CustomDataTableHeader[];

  bookingSplitId: number | null;
  bookingSplitItemIds: Array<number>;
  recordLocator: string;
}

export const BookingsState: BookingsStateInterface = {
  bookings: [],
  isBookingsLoading: false,
  bookingsDataTableOptions: new DefaultDataOptions(),
  countBookings: 0,
  bookingsFilters: null,
  recordStampId: null,
  recordStampBooking: null,
  selectedBookingsFirstNumbers: [],
  bookingCloneBaseId: null,
  bookingCloneNewRecordLocator: null,
  bookingDetailsId: null,
  bookingDetails: null,
  bookingItemToCancelId: null,
  bookingItemToCancelTicketNumber: null,
  isEditTemp: false,
  editTempDataTableOptions: new DefaultDataOptions(),
  editTempFilters: null,
  tableHeaders: [],

  bookingSplitId: null,
  bookingSplitItemIds: [],
  recordLocator: "",
};

const BookingsModule = defineModule({
  state: BookingsState as BookingsStateInterface,
  modules: { bookingsGroup: BookingsGroupModule },
  namespaced: true as const,
  getters: {},
  mutations: {
    setSelectedBookingsFirstNumbers(state, param: Array<string>) {
      state.selectedBookingsFirstNumbers = param;
    },
    setTableHeaders(state, param: CustomDataTableHeader[]) {
      state.tableHeaders = param;
    },
    setBookingsDataTableOptions(state, param: DataOptions) {
      state.bookingsDataTableOptions = JSON.parse(JSON.stringify(param));
    },
    startBookingsLoading(state) {
      state.isBookingsLoading = true;
    },
    setBookings(state, param: { items: Array<BookingsListItem>; count: number }) {
      state.bookings = param.items;
      state.countBookings = param.count;
      state.isBookingsLoading = false;
    },
    setBookingsFilters(state, param: Array<CriterionItemRequest>) {
      state.bookingsFilters = param;
    },
    toggleChecked(state, param: BookingSelectResponse) {
      if (state.bookings != null) {
        for (const booking of state.bookings) {
          if (param.bookingIds.includes(booking.id)) {
            booking.bookingIsSelected = !booking.bookingIsSelected;
          }
          for (const bookingItem of booking.bookingItems) {
            if (param.bookingItemIds.includes(bookingItem.id)) {
              bookingItem.bookingItemIsSelected = !bookingItem.bookingItemIsSelected;
            }
          }
        }
      }
    },
    setRecordStampId(state, recordStampId: number | null) {
      state.recordStampId = recordStampId;
    },
    setRecordStampBooking(state, recordStampBooking: BookingsListItem | null) {
      state.recordStampBooking = recordStampBooking;
      if (
        recordStampBooking !== null &&
        recordStampBooking.bookingItems !== null &&
        recordStampBooking.bookingItems.length > 0
      ) {
        state.recordStampId = recordStampBooking.bookingItems[0].recordStampId;
      }
    },
    setBookingCloneBaseId(state, param: number | null) {
      state.bookingCloneBaseId = param;
    },
    setBookingCloneNewRecordLocator(state, param: string | null) {
      state.bookingCloneNewRecordLocator = param;
    },
    setBookingDetailsId(state, param: number | null) {
      state.bookingDetailsId = param;
    },
    setBookingDetails(state, param: null | BookingDetails) {
      state.bookingDetails = param;
    },
    setBookingSplitId(state, param: number | null) {
      state.bookingSplitId = param;
    },
    setBookingSplitItemIds(state, param: Array<number>) {
      state.bookingSplitItemIds = param;
    },
    setRecordLocator(state, param: string) {
      state.recordLocator = param;
    },
    setBookingToCancel(state, param: null | { id: number; ticketNumber: string | null }) {
      if (param === null) {
        state.bookingItemToCancelId = null;
        state.bookingItemToCancelTicketNumber = null;
      } else {
        state.bookingItemToCancelTicketNumber = param.ticketNumber;
        state.bookingItemToCancelId = param.id;
      }
    },
    setIsEditTemp(state, param: boolean) {
      state.isEditTemp = param;
      if (param) {
        state.editTempFilters = state.bookingsFilters === null ? null : _.cloneDeep(state.bookingsFilters);
        state.editTempDataTableOptions = state.bookingsDataTableOptions;
      }
    },
  },
  actions: {
    async getBookingsTable(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.startBookingsLoading();
      let response;
      if (state.isEditTemp) {
        response = await getBookingsTable({
          count: state.editTempDataTableOptions.itemsPerPage,
          page: state.editTempDataTableOptions.page - 1,
          criterions: state.editTempFilters === null ? null : _.cloneDeep(state.editTempFilters),
          filterName: FILTER_NAMES.BookingsFilter,
        });
        commit.setIsEditTemp(false);
      } else {
        response = await getBookingsTable({
          count: state.bookingsDataTableOptions.itemsPerPage,
          page: state.bookingsDataTableOptions.page - 1,
          criterions: state.bookingsFilters === null ? null : _.cloneDeep(state.bookingsFilters),
          filterName: FILTER_NAMES.BookingsFilter,
        });
      }
      commit.setBookings({ items: response.items, count: response.totalCount });
    },
    async toggleSelectBooking(context, bookingId: number) {
      const { commit } = _moduleActionContext(context);
      const response = await toggleSelectBooking(bookingId);
      commit.toggleChecked(response);
    },
    async toggleSelectBookingItem(context, bookingItemId: number) {
      const { commit } = _moduleActionContext(context);
      const response = await toggleSelectBookingItem(bookingItemId);
      commit.toggleChecked(response);
    },
    async unselectAllBookings(context) {
      const { dispatch } = _moduleActionContext(context);
      await postUnselectAllBookings();
      await dispatch.getBookingsTable();
    },
    async getSelectedBookingsFirstNumbers(context) {
      const { commit } = _moduleActionContext(context);
      commit.setSelectedBookingsFirstNumbers([]);
      const selectedBookingItemsFirstNumbers = await getSelectedBookingItemsFirstNumbers();
      commit.setSelectedBookingsFirstNumbers(selectedBookingItemsFirstNumbers);
    },
    async cancelBookingItem(context, model: CancelBookingItem) {
      const { dispatch, state, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      model.bookingItemId = state.bookingItemToCancelId as number;
      await postCancelBookingItem(model);
      await dispatch.getBookingsTable();
    },
    async cancelSelectedBookingItems(context, model: CancelBookingItem) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      await postCancelSelectedBookingItems(model);
      await dispatch.getBookingsTable();
    },
    async openBookingCloneDialog(context, bookingId: number) {
      const { commit } = _moduleActionContext(context);
      commit.setBookingCloneNewRecordLocator(null);
      commit.setBookingCloneBaseId(bookingId);
    },
    async generateRecordLocatorForBookingClone(context) {
      const { commit } = _moduleActionContext(context);
      const generatedRecordLocator = await generateBookingRecordLocator();
      commit.setBookingCloneNewRecordLocator(generatedRecordLocator);
    },
    async cloneBooking(context) {
      const { rootDispatch, commit, state } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.bookingCloneNewRecordLocator !== null && state.bookingCloneBaseId !== null) {
        const sessionId = await cloneBooking(
          state.bookingCloneBaseId as number,
          state.bookingCloneNewRecordLocator as string
        );
        rootDispatch.bookingAddEdit.cloneBooking(sessionId);
        commit.setBookingCloneNewRecordLocator(null);
        commit.setBookingCloneBaseId(null);
        await routerInstance.push({ name: "Clone booking" });
      }
    },
    async cloneBookingItem(context, bookingItemId: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      let result = null;
      if (!(await rootDispatch.user.hasPermission(PERMISSIONS.BookingsChainedClone))) result = false;
      else {
        result = (await rootDispatch.popup.addConfirm({
          title: filterCapitalize(i18n.t("warning")),
          content: filterCapitalize(i18n.t("isBookingShouldBeCloneChainedThatRunManually")),
          customButtons: [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: null,
            },
            {
              text: filterCapitalize(i18n.t("no")),
              color: "warning",
              outlined: true,
              value: false,
            },
            {
              text: filterCapitalize(i18n.t("yes")),
              color: "warning",
              value: true,
            },
          ],
        })) as boolean | null;
      }
      if (result !== null) {
        await cloneBookingItem(bookingItemId, result);
        await dispatch.getBookingsTable();
      }
    },
    async openDetails(context, bookingId: number) {
      const { commit } = _moduleActionContext(context);
      commit.setBookingDetailsId(bookingId);
      const bookingDetails = await getBookingDetails(bookingId);
      commit.setBookingDetails(bookingDetails);
    },
    async openSplitDialog(context, params: { bookingId: number; recordLocator: string; itemIds: Array<number> }) {
      const { commit } = _moduleActionContext(context);
      const response = await getRecordLocatorForSplittedBooking(params.recordLocator);
      commit.setRecordLocator(response);
      commit.setBookingSplitId(params.bookingId);
      commit.setBookingSplitItemIds(params.itemIds);
    },
    async splitBooking(context) {
      const { state, dispatch } = _moduleActionContext(context);
      if (state.bookingSplitId && state.bookingSplitItemIds.length && state.recordLocator) {
        await postSplitBooking({
          recordLocator: state.recordLocator,
          bookingId: state.bookingSplitId,
          bookingItemIds: state.bookingSplitItemIds,
        });
        await dispatch.getBookingsTable();
      }
    },
    async changeHasCostDocument(context, params: { bookingItemId: number; value: boolean }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const result = (await rootDispatch.popup.addConfirm({
        title: filterCapitalize(i18n.t("warning")),
        content: filterCapitalize(i18n.t("areYouSureYouWantToChangeThisOption")),
        customButtons: [
          {
            text: filterCapitalize(i18n.t("no")),
            color: "warning",
            outlined: true,
            value: false,
          },
          {
            text: filterCapitalize(i18n.t("yes")),
            color: "warning",
            value: true,
          },
        ],
      })) as boolean;
      if (result) {
        await changeHasCostDocument(params.bookingItemId, params.value);
        await dispatch.getBookingsTable();
      }
    },
    async changeBlockToInvoiceStatus(context, bookingItemId: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const result = (await rootDispatch.popup.addConfirm({
        title: filterCapitalize(i18n.t("warning")),
        content: filterCapitalize(i18n.t("areYouSureYouWantToUnlockBookingItemToInvoice")),
        customButtons: [
          {
            text: filterCapitalize(i18n.t("no")),
            color: "warning",
            outlined: true,
            value: false,
          },
          {
            text: filterCapitalize(i18n.t("yes")),
            color: "warning",
            value: true,
          },
        ],
      })) as boolean;
      if (result) {
        await unlockBookingItemToInvoice(bookingItemId);
        await dispatch.getBookingsTable();
      }
    },
  },
});

export default BookingsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, BookingsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, BookingsModule);
